<template>
  <v-container v-if="initialized" fluid>
    <dialog-confirm ref="reprocess-route-dialog">
      {{ $t('route_reprocessing_warning') }}
    </dialog-confirm>
    <customer-context-prompt :resource="route"/>
    <div class="d-flex align-center mb-3">
      <h2 class="d-flex align-center mb-0">
        <v-icon color="black" size="40">mdi-map-marker-path</v-icon>
        <span class="ml-3">
          <span v-if="route.route_plan_id">{{ $tc('route') }} </span>{{ route.name }}
        </span>
        <router-link :to="`/vehicle/${route.vehicle_id}`" class="subheader d-flex align-center ml-4">
          <v-icon size="35" color="primary" class="mr-2">mdi-truck</v-icon>
          {{ route.vehicle.name }}
        </router-link>
      </h2>
      <route-violations-dialog :route="route"/>
      <route-warnings-dialog :route="route"/>
      <forecast-violations-dialog :route="route" :error-messages="forecastStatusErrorMessages"/>
    </div>
    <div class="d-flex align-center mt-n1 mb-4">
      <v-chip v-if="route.archived" dark small label class="mr-2">
        {{ $t('archived') }}
      </v-chip>
      <v-chip small>
        <multi-time-display v-if="route.start" :time="route.start"/>
        <span v-else class="not-set">{{ $t('not_set') }}</span>
      </v-chip>
      <span class="ml-2"> - </span>
      <v-chip small class="ml-2">
        <multi-time-display v-if="route.end" :time="route.end"/>
        <span v-else-if="!route.start">{{ $t('not_set') }}</span>
        <span v-else>{{ $t('in_progress' )}}</span>
      </v-chip>
      <span class="ml-2">~</span>
      <v-chip small class="ml-2">
        <span v-if="route.duration">{{ route.duration | humanizeDuration(({ numberOfSignificantParts: 2, separator: ', ' })) }}</span>
        <span v-else>{{ $t('not_set') }}</span>
      </v-chip>

      <div v-if="showWhoCreated && route.creation_audit_log" class="ml-2">
        <!-- User Created -->
        <div v-if="get(route, 'creation_audit_log.user_id')">
          <v-tooltip top color="primary-900">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-chip small dark color="primary">
                  {{ $t('user_created') }}
                </v-chip>
              </span>
            </template>
            <span>
              {{$t('created_by')}}
              <span>{{ route.creation_audit_log.user_name}}</span>
              {{$t('on')}}
              <multi-time-display :time="route.creation_audit_log.timestamp" />
            </span>
          </v-tooltip>
        </div>
        <!-- System Created -->
        <div v-else>
          <v-tooltip top color="primary-900">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-chip small dark color="primary">
                  {{ $t('system_created') }}
                </v-chip>
              </span>
            </template>
            <span>
              {{$t('created')}}: <multi-time-display :time="route.creation_audit_log.timestamp" />
            </span>
          </v-tooltip>
        </div>
      </div>

      <!-- Show deleted status -->
      <div v-if="route.deleted" class="ml-2">
        <v-tooltip v-if="route.deletion_audit_log" top color="primary-900">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-chip small dark color="danger">
                {{ $t('deleted') }}
              </v-chip>
            </span>
          </template>
          <span>
            {{$t('deleted_by')}}
            <span>{{ route.deletion_audit_log.user_name}}</span>
            {{$t('on')}}
            <multi-time-display :time="route.deletion_audit_log.timestamp" />
          </span>
        </v-tooltip>
        <v-chip v-else small dark color="danger">
          {{ $t('deleted') }}
        </v-chip>
      </div>
    </div>

    <div class="d-flex mb-3">
      <temperature-chart
        ref="temperature-chart"
        :height="320"
        :data="temperatureChartData"
        :show-deliveries="showDeliveries"
        :initial-column-visibility="chartColumnVisibility"
        @legend-click="chartLegendClicked"/>
      <div class="command-links pl-4">
        <v-checkbox
          class="ml-3"
          :label="$t('show_deliveries_on_chart')"
          v-model="showDeliveries"
          v-show="temperatureChartData.hasDeliveries()"/>
        <v-subheader class="mt-n4">{{ $tc('action', 2) }}</v-subheader>
        <v-btn :disabled="route.archived" :to="`/route/update/${route.id}/`" text block color="primary" class="justify-start">
          <v-icon>mdi-pencil</v-icon>
          <span class="ml-3">{{ $t('edit_route') }}</span>
        </v-btn>
        <v-btn
          v-if="route.end"
          :loading="isDownloading"
          @click="exportExcel()"
          text
          block
          color="primary"
          class="justify-start">
          <v-icon>mdi-export</v-icon>
          <span class="ml-3">{{ $t('export_route_data') }}</span>
        </v-btn>
        <div v-if="user.can('view_rootentity')">
          <v-subheader>{{ $t('internal_only') }}</v-subheader>
          <v-btn @click="exportDump()" :loading="isDumping" text block color="primary" class="justify-start">
            <v-icon>mdi-database</v-icon>
            <span class="ml-3">{{ $t('dump_route_data') }}</span>
          </v-btn>
          <v-btn :disabled="route.archived" @click="reprocessRoute()" :loading="isReprocessing" text block color="primary" class="justify-start">
            <v-icon>mdi-arrow-u-right-top-bold</v-icon>
            <span class="ml-3">{{ $t('reprocess_route') }}</span>
          </v-btn>
        </div>
      </div>
    </div>
    <v-card outlined rounded class="mt-6">
      <v-card-text>
        <v-tabs color="primary-700" show-arrows v-model="activeTab">
          <v-tabs-slider color="primary-700"/>
          <!-- Location tab -->
          <v-tab :href="`#${tabs.LOCATIONS}`">
            {{ $tc('location', 2) }}
          </v-tab>
          <v-tab-item :value="tabs.LOCATIONS" class="vf-border-top tab-content" :transition="false">
            <route-locations v-if="gpsEnabled && size(route.route_locations) > 0" :route="route"/>
          </v-tab-item>
          <!-- Product tab-->
          <v-tab :href="`#${tabs.PRODUCTS}`">
            {{ $tc('product', 2) }}
          </v-tab>
          <v-tab-item :value="tabs.PRODUCTS" class="vf-border-top tab-content pt-5" :transition="false">
            <route-product-table :route-products="route.route_products"/>
          </v-tab-item>
          <!-- Root Cause tab -->
          <v-tab :href="`#${tabs.ROOT_CAUSE}`">
            {{ $t('root_cause_analysis') }}
            <vf-tooltip v-if="assessmentPending" :text="$tc('pending_assessment')">
              <v-icon color="warning" small class="ml-1">mdi-alert</v-icon>
            </vf-tooltip>
          </v-tab>
          <v-tab-item :value="tabs.ROOT_CAUSE" class="vf-border-top tab-content pt-3" :transition="false">
            <v-alert v-if="assessmentPending" type="warning" dense text class="mt-3 mb-0">
              {{ $t('root_cause_assessment_required') }}
            </v-alert>
            <route-assessment-form
              class="mt-3"
              :model="route.route_assessment"
              :route="route"
              :customer-assessment-terms="route.customer.assessment_terms"
              :require-probed-temperatures="customerContext.getSetting('requireManualTempsInRootCauseAssessment')"
              :show-violation-summary="hasFatalViolations"
              v-on:saved="updateRouteAssessment($event)" />
          </v-tab-item>
          <!-- Recorded temperatures -->
          <v-tab v-if="showRouteProductRecordedTemperatures" :href="`#${tabs.MANUAL_TEMPS}`">
            {{ $t('manual_temps') }}
          </v-tab>
          <v-tab-item
            v-if="showRouteProductRecordedTemperatures"
            :value="tabs.MANUAL_TEMPS"
            class="vf-border-top tab-content pt-5"
            :transition="false">
            <recorded-temp-table :route-products="route.route_products"/>
          </v-tab-item>
          <!-- Notes tab -->
          <v-tab :href="`#${tabs.NOTES}`">
            {{ $tc('note', 2) }}
          </v-tab>
          <v-tab-item :value="tabs.NOTES" class="vf-border-top tab-content pt-3" :transition="false">
            <h6 class="pt-2">{{ $t('add_a_note_to_route') }}:</h6>
            <resource-notes
              ref="routeNotes"
              v-model="newNoteContent"
              :notes="route.notes"
              v-on:create-note="createNote($event)" />
          </v-tab-item>
          <!-- Route Data tab -->
          <v-tab v-if="!route.archived" :href="`#${tabs.ROUTE_DATA}`">
            {{ $t('route_data') }}
          </v-tab>
          <v-tab-item v-if="!route.archived" :value="tabs.ROUTE_DATA" class="vf-border-top tab-content" :transition="false">
            <route-data-table ref="route-data" :route="route"/>
          </v-tab-item>
          <!-- Audit Log tab -->
          <v-tab :href="`#${tabs.AUDIT_LOG}`">
            {{ $tc('audit_log') }}
          </v-tab>
          <v-tab-item :value="tabs.AUDIT_LOG" class="vf-border-top tab-content pt-3" :transition="false">
            <v-sheet class="pa-1 mt-2">
              <v-expansion-panels accordion v-model="auditLogTypeIndex">
                <v-expansion-panel key="route-audit-logs">
                  <v-expansion-panel-header class="text-subtitle-1">
                    <span>
                      <v-icon>mdi-road-variant</v-icon>
                      {{ $tc('route', 2) }}
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <audit-log
                      ref="route-audit-log"
                      autoload
                      :table-controls="['actor', 'date_range']"
                      :table-columns="['user', 'customer', 'action', 'timestamp']"
                      :initSearchCriteria="routeAuditLogSearchCriteria"/>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel key="route-product-audit-logs" @click="loadAuditLogRouteProductIds">
                  <v-expansion-panel-header class="text-subtitle-1">
                    <span>
                      <v-icon>mdi-food</v-icon>
                      {{ $tc('route_product', 2) }}
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-progress-circular v-if="isEmpty(auditLogRouteProductIds)"/>
                    <audit-log
                      v-else
                      ref="route-product-audit-log"
                      autoload
                      :table-controls="['actor', 'date_range']"
                      :table-columns="['user', 'customer', 'primary_key', 'action', 'timestamp']"
                      :initSearchCriteria="routeProductAuditLogSearchCriteria"/>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-sheet>
          </v-tab-item>
          <!-- Notification History -->
          <v-tab v-if="this.user.can('view_rootentity')" :href="`#${tabs.NOTIFICATION_HISTORY}`">
            {{ $tc('notification_history') }}
          </v-tab>
          <v-tab-item
            v-if="this.user.can('view_rootentity')"
            :value="tabs.NOTIFICATION_HISTORY"
            class="vf-border-top tab-content pt-3"
            :transition="false">
            <notification-history ref="notification-history" :initSearchCriteria="notificationSearchCriteria"/>
          </v-tab-item>
          <v-tab v-if="this.user.can('view_rootentity')" :href="`#${tabs.DATA_QUALITY}`">
            {{ $tc('route_data_quality') }}
          </v-tab>
          <v-tab-item
            v-if="this.user.can('view_rootentity')"
            :value="tabs.DATA_QUALITY"
            class="vf-border-top tab-content pt-3"
            :transition="false">
            <route-data-quality-view ref="route-data-quality" :route="route"/>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>
  </v-container>
  <v-container v-else fluid>
    <div class="d-flex align-center mb-6">
      <v-skeleton-loader type="avatar"/>
      <v-skeleton-loader class="ml-3" width="200" type="text"/>
      <v-skeleton-loader class="ml-3" width="150" type="text"/>
      <v-skeleton-loader class="ml-4" type="avatar"/>
    </div>
    <div class="d-flex align-center mt-n1 mb-4">
      <v-skeleton-loader type="chip"/>
      <v-skeleton-loader class="ml-3" type="chip"/>
      <v-skeleton-loader class="ml-3" type="chip"/>
    </div>
    <div class="d-flex mb-6">
      <v-skeleton-loader class="flex" type="image"/>
      <div class="command-links pl-5">
        <v-skeleton-loader type="chip" width="150"/>
        <v-skeleton-loader class="mt-2" type="chip" width="150"/>
      </div>
    </div>
    <div>
      <v-skeleton-loader type="card"/>
    </div>
  </v-container>
</template>

<script>
import CustomerContextPrompt from '@/components/shared/CustomerContextPrompt'
import TemperatureChart from '@/components/shared/chart/TemperatureChart'
import RouteLocations from '@/components/route/RouteLocations'
import RouteProductTable from '@/components/route/RouteProductTable'
import RecordedTempTable from '@/components/route/RecordedTempTable'
import ResourceNotes from '@/components/shared/ResourceNotes'
import RouteDataTable from '@/components/route/RouteDataTable'
import AuditLog from '@/components/audit-log/AuditLog'
import DialogConfirm from '@/components/shared/DialogConfirm'
import NotificationHistory from '@/components/notification/NotificationHistory'
import RouteAssessmentForm from '@/components/root-cause/RouteAssessmentForm'
import RouteDataQualityView from '@/components/route/RouteDataQualityView'
import VfTooltip from '@/components/shared/VfTooltip'
import RouteViewTabs from '@/components/route/RouteViewTabs'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import RouteViolationsDialog from '@/components/route/RouteViolationsDialog'
import RouteWarningsDialog from '@/components/route/RouteWarningsDialog'
import ForecastViolationsDialog from '@/components/route/ForecastViolationsDialog'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import ChartMixin from '@/components/mixins/ChartMixin'
import FileSaver from 'file-saver'
import Moment from 'moment-timezone'
import { get, size, sortBy, isEmpty, includes, map, filter, each, some, trimStart } from 'lodash'

export default {
  name: 'RouteView',
  components: {
    CustomerContextPrompt,
    TemperatureChart,
    RouteLocations,
    RouteProductTable,
    RecordedTempTable,
    ResourceNotes,
    RouteDataTable,
    AuditLog,
    DialogConfirm,
    NotificationHistory,
    RouteAssessmentForm,
    RouteDataQualityView,
    MultiTimeDisplay,
    RouteViolationsDialog,
    RouteWarningsDialog,
    ForecastViolationsDialog,
    VfTooltip
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin,
    DateTimeUtilsMixin,
    TemperatureMixin,
    ChartMixin
  ],
  data () {
    return {
      size: size,
      tabs: RouteViewTabs,
      // The tab on display. Mirrored in the URL fragment so tabs can be linked to directly (VF-4301).
      activeTab: RouteViewTabs.LOCATIONS,
      initialized: false,
      // Set in beforeDestroy so fetchRoute() can tell that the user navigated away while it was awaiting.
      componentDestroyed: false,
      route: null,
      // the processed chart data used in the temperature chart
      temperatureChartData: null,
      forecastStatusErrorMessages: [],
      newNoteContent: '',
      isReloadingRoute: false,
      isDownloading: false,
      isDumping: false,
      isReprocessing: false,
      // Store any pending updates (like from the time slider) in here
      pendingUpdates: {},
      // A special-case array to hold all route product ids (including soft-deleted) for the Audit Log.
      auditLogRouteProductIds: [],
      // Forecast data we retrieve from the back end in case we need to
      // re-render when the customer context changes
      routeTemperatureForecastData: [],
      showDeliveries: true,
      chartColumnVisibility: {},
      activeViolationTab: 0,
      dialog: false,
      // default to routes when audit log is opened
      auditLogTypeIndex: 0
    }
  },
  methods: {
    get: get,
    isEmpty: isEmpty,
    /**
     * Load the route.
     * @returns {Promise<void>}
     */
    async fetchRoute () {
      this.initialized = false
      const expand = [
        'vehicle',
        'interpolated_sensor_data',
        'route_products.product',
        'route_plan.route_plan_locations',
        'route_locations.location',
        'route_locations.route',
        'route_violations.route_product.label',
        'route_violations.route_product.product',
        'route_assessment.notes.user_email',
        'customer.assessment_terms',
        'customer.customer_settings',
        'forecast_route_violations.route_product.label',
        'notes.user_email',
        'creation_audit_log',
        'deletion_audit_log',
        'route_locations.simulated_temperatures_at_arrival',
        'route_locations.route_product_recorded_temperatures',
        'route_locations.location'
      ]
      try {
        this.route = await this.$orm('Route').find(this.$route.params.id, {
          params: {
            expand: expand.join(',')
          }
        })
        // The user may have navigated away while the (heavily expanded) route request was in flight. Continuing
        // would operate on a torn-down instance: vue-i18n nulls out $i18n on destroy so the $tc() below throws, and
        // the resulting handleError() would raise the generic error modal over whatever page they moved on to.
        if (this.componentDestroyed) {
          return
        }
        if (!this.route) {
          console.error('[veroFresh] Route ORM returned undefined despite API 200 — clearing persisted state and reloading')
          window.localStorage.removeItem('veroFreshApp')
          window.location.reload()
          return
        }
        // Filter out ignored route locations
        this.route.route_locations = filter(this.route.route_locations, routeLocation => {
          return (get(routeLocation, 'location.type') !== 'ignored')
        })
        // Sort the route locations (do we still need to do this? Carried over from older code)
        this.route.route_locations = sortBy(this.route.route_locations, ['vehicle_arrival', 'eta'])
        // Get the temperature forecasts, we need them for formatRouteChartData if we are to show them in the chart.
        await this.fetchTemperatureForecasts()
        // Same again - the forecast requests are per route product, so this await can be long on a large route.
        if (this.componentDestroyed) {
          return
        }
        // Process the raw route data into the format used in TemperatureChart
        this.temperatureChartData = this.formatRouteChartData(
          this.route,
          this.route.interpolated_sensor_data,
          this.routeTemperatureForecastData
        )
        this.updateBrowserTitle(this.$tc('route') + ': ' + this.route.name)
        this.initialized = true
        // Only now do we know which tabs this route has, so a deep-linked fragment can be resolved.
        this.syncTabFromHash()
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Show the tab named by the URL fragment, e.g. /route/5608347#rca opens the Root Cause tab (VF-4301).
     *
     * A fragment which doesn't name a tab available on this route - an unknown one, or a tab hidden by the
     * customer's settings or the user's permissions - falls back to the first tab, and the activeTab watcher
     * rewrites the fragment to match.
     *
     * @return undefined
     */
    syncTabFromHash () {
      const hash = trimStart(this.$route.hash, '#')
      // No fragment: leave the default tab selected (and leave the URL alone).
      if (hash === '') {
        return
      }
      const tab = includes(this.availableTabs, hash) ? hash : this.availableTabs[0]
      this.activeTab = tab
      // Selecting the tab we're already on doesn't fire the activeTab watcher, so write the fragment from here
      // too - otherwise a fragment naming a tab that isn't there would stay in the URL.
      this.writeTabToHash(tab)
    },
    /**
     * Point the URL fragment at the given tab, without adding a history entry: switching tabs shouldn't fill up
     * the back button.
     *
     * @param {string} tab
     * @return undefined
     */
    writeTabToHash (tab) {
      if (tab == null || this.$route.hash === `#${tab}`) {
        return
      }
      this.$router.replace({ hash: `#${tab}` }).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          throw error
        }
      })
    },
    /**
     * Fetch the temperature forecast data (if available) for each route product.
     * @returns {Promise<void>}
     */
    async fetchTemperatureForecasts () {
      // Don't request forecasts on routes which have ended (we double-check the end time here because routes can be
      // created with an end time in the future)
      if (this.route.end != null && Moment().isAfter(this.route.end)) {
        return
      }
      // Likewise, if the route doesn't have a route plan attached then we can't forecast temperatures, so return
      // early.
      if (this.route.route_plan_id == null) {
        return
      }
      // Finally, we may have routes which were deleted before an end was set, in such a case we can also return early
      if (this.route.deleted) {
        return
      }
      // We can fetch each route product's forecast asynchronously then return the combined results.
      // Backend now determines scope automatically based on customer settings
      const forecastRequests = []
      each(this.route.route_products, routeProduct => {
        forecastRequests.push(this.$httpAdapter({
          url: this.$orm('RouteProduct').endpoint + '/' + routeProduct.id + '/temperature-forecast',
          method: 'GET'
        }).then((resp) => {
          this.routeTemperatureForecastData[routeProduct.product.id] = resp.data
        }).catch((error) => {
          // If the API returns an unprocessable request response then populate the error display object, so we can show
          // the user why no forecast is available at this time.
          // Note: a failed forecast must never take the whole route view down, so read the status defensively - it is
          // undefined when the request never got a response at all. (VF-4310)
          const status = get(error, 'response.status')
          if (status === 422) {
            this.forecastStatusErrorMessages.push({
              route_product: routeProduct,
              error_message: get(error, 'response.data.message')
            })
          } else if (status === 403) {
            // There's really not much to do here... perhaps the user's customer settings are out of sync? In any
            // case we don't want to show the user an error message, fail silently
          }
        }))
      })
      await Promise.all(forecastRequests)
    },
    /**
     * For audit logs, we need to make a special-case API request to get *every* route product id attached to the route,
     * including soft-deleted route products. We want to defer loading this until we need to.
     * @returns {Promise<void>}
     */
    async loadAuditLogRouteProductIds () {
      if (this.user.can('view_auditlog')) {
        const routeProducts = await this.$orm('RouteProduct').findAll({
          fields: 'id',
          deleted_scope: 'include',
          route_id: this.route.id,
          customer_id: this.route.customer_id
        })
        this.auditLogRouteProductIds = map(routeProducts.getItems(), x => x.id)
      }
    },
    /**
     * Re-process temperature data if the temperature scale has changed
     * @param newCustomerContext CustomerRecord
     * @param oldCustomerContext CustomerRecord
     *
     * @return undefined
     */
    customerContextChangeHandler (newCustomerContext, oldCustomerContext) {
      if (newCustomerContext.tempFormat() !== oldCustomerContext.tempFormat()) {
        this.temperatureChartData = this.formatRouteChartData(
          this.route,
          this.route.interpolated_sensor_data,
          this.routeTemperatureForecastData
        )
      }
    },
    /**
     * Create a note attached to this route
     */
    createNote (note) {
      if (isEmpty(note)) {
        this.$store.dispatch(
          'globalMessagingModule/showErrorFlashBanner',
          this.$t('note_body_required')
        )
        this.$refs.routeNotes.newNoteSaveComplete()
        return
      }
      const newNote = {
        resource: 'route',
        entity_id: this.route.id,
        user_id: this.user.id,
        note: note,
        context: 'frontend/form/route'
      }

      this.$orm('Note')
        .createOrUpdate(newNote, { params: { expand: 'user_email', fields: 'id,note,created,user_email' } })
        .then(note => {
          this.route.notes.push(note)
          this.$refs.routeNotes.resetNoteBody()
          this.$refs.routeNotes.newNoteSaveComplete()

          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$tc('note')} ${this.$t('generic_save_success')}`
          )
        })
        .catch(error => {
          this.$refs.routeNotes.newNoteSaveComplete()
          this.handleError(error)
        })
    },
    /**
     * Calls the API excel export method and downloads the result
     *
     * @return null
     */
    exportExcel () {
      // Update the UI to show the spinner on the download button
      this.isDownloading = true

      this.$gtag.event('download', { event_category: 'Route' })

      this.$httpAdapter({
        url: `${this.$orm('Route').endpoint}/${this.route.id}/export`,
        method: 'GET',
        headers: {
          Accept: 'application/vnd.ms-excel'
        },
        responseType: 'blob' // important
      }).then((response) => {
        // Parse the file name from the content-disposition header
        const headerLine = response.headers['content-disposition']
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex)

        FileSaver.saveAs(new Blob([response.data]), fileName)
      }).catch(error => {
        this.handleError(error)
      }).finally(() => {
        this.isDownloading = false
      })
    },
    /**
     * Make an API request for a SQL dump of this route
     * @return undefined
     */
    exportDump () {
      this.isDumping = true
      this.$httpAdapter({
        url: `${this.$orm('Route').endpoint}/${this.route.id}/dump`,
        method: 'GET'
      }).then((response) => {
        // Parse the file name from the content-disposition header
        const headerLine = response.headers['content-disposition']
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex)

        FileSaver.saveAs(new Blob([response.data]), fileName)
      }).catch(error => {
        this.handleError(error)
      }).finally(() => {
        this.isDumping = false
      })
    },
    /**
     * Make an API request to reprocess the vehicle data for this route.
     */
    async reprocessRoute () {
      if (await this.$refs['reprocess-route-dialog'].confirm()) {
        this.isReprocessing = true
        try {
          await this.$httpAdapter({
            url: `${this.$orm('Route').endpoint}/${this.route.id}/reprocess-data`,
            method: 'GET'
          })
          await this.$store.dispatch('globalMessagingModule/showSuccessFlashBanner', this.$t('route_reprocessing_job_queued'))
        } catch (error) {
          if (get(error, 'response.status') === 422) {
            const overlappingRouteId = get(error, 'response.data.errors.overlapping_route_id')
            await this.$store.dispatch(
              'globalMessagingModule/showErrorFlashBanner',
              this.$t('could_not_reprocess_overlapping_route', { overlappingRouteId: overlappingRouteId })
            )
          } else {
            this.handleError(error)
          }
        } finally {
          this.isReprocessing = false
        }
      }
    },

    /**
     * When the assessment is created in the child component update the route
     * here so that the banner, accordion text, and child component all stay in
     * sync
     *
     * @return undefined
     */
    async updateRouteAssessment (routeAssessment) {
      this.route.route_assessment = routeAssessment
      await this.$store.dispatch(
        'globalMessagingModule/showSuccessFlashBanner',
        `${this.$i18n.tc('route_assessment')} ${this.$i18n.t('generic_save_success')}`
      )
      // The route just left the pending assessment queue, so the side nav count is now stale (VF-4301).
      await this.$store.dispatch('sideNavStateModule/refreshUnassessedRouteCount', this.customerContextId)
    },

    /**
     * Event handler for when columns are clicked in the temperature chart legend.
     */
    chartLegendClicked (columnId) {
      const currentValue = get(this.chartColumnVisibility, columnId, true)
      this.chartColumnVisibility[columnId] = !currentValue
      console.log(this.chartColumnVisibility)
      this.$store.dispatch('vueRouteStateModule/setState', {
        chartColumnVisibility: this.chartColumnVisibility,
        $routeName: 'route-plan-view'
      })
    },

    /**
     * Retrieve the saved state in vueRouteStateModule for this page
     */
    restoreState () {
      const priorState = this.$store.getters['vueRouteStateModule/getStateByRouteName']('route-plan-view')
      let chartColumnVisibility = get(priorState, 'chartColumnVisibility')
      // If nothing defined, set default values
      if (chartColumnVisibility === undefined) {
        const defaultObject = {}
        defaultObject[this.$t('cooler_setpoint')] = false
        defaultObject[this.$t('freezer_setpoint')] = false
        chartColumnVisibility = defaultObject
      }
      this.chartColumnVisibility = chartColumnVisibility
    }
  },
  computed: {
    /**
     * Whether this route still needs a root cause assessment, which the Root Cause tab flags with a warning icon.
     *
     * `assessment_pending` is the API's own answer: the violation is fatal and its route has no assessment
     * (RouteViolation::getRouteCause is a hasOne to RouteAssessment keyed on route_id, so it is route-level despite
     * living on the violation). The local route_assessment check in front of it is deliberate rather than redundant:
     * it clears the icon the moment the form on this page saves, without re-fetching the route.
     *
     * @returns {boolean}
     */
    assessmentPending () {
      if (get(this.route, 'route_assessment') != null) {
        return false
      }
      return some(get(this.route, 'route_violations', []), violation => violation.assessment_pending)
    },

    /**
     * Whether the route has any fatal violation at all - assessed or not. Drives the form's violation summary, so
     * someone reading a completed assessment can still see what it was for.
     *
     * @returns {boolean}
     */
    hasFatalViolations () {
      return some(
        get(this.route, 'route_violations', []),
        violation => violation.level === RouteViolationRecord.LEVEL_FATAL
      )
    },

    /**
     * The tabs actually rendered for this route, in display order. This has to stay in step with the v-ifs on the
     * tabs in the template, so that a URL fragment can only ever select a tab the user can see.
     *
     * @returns {string[]}
     */
    availableTabs () {
      if (this.route == null) {
        return []
      }
      const tabs = [
        RouteViewTabs.LOCATIONS,
        RouteViewTabs.PRODUCTS,
        RouteViewTabs.ROOT_CAUSE
      ]
      if (this.showRouteProductRecordedTemperatures) {
        tabs.push(RouteViewTabs.MANUAL_TEMPS)
      }
      tabs.push(RouteViewTabs.NOTES)
      if (!this.route.archived) {
        tabs.push(RouteViewTabs.ROUTE_DATA)
      }
      tabs.push(RouteViewTabs.AUDIT_LOG)
      if (this.user.can('view_rootentity')) {
        tabs.push(RouteViewTabs.NOTIFICATION_HISTORY, RouteViewTabs.DATA_QUALITY)
      }
      return tabs
    },

    /**
     * Whether to show the 'User Created'/'System Created' chip
     * @returns {boolean}
     */
    showWhoCreated () {
      return some(this.user.roles, x => x.code === 'root')
    },

    /**
     * @return Boolean
     */
    gpsEnabled () {
      return get(this.route.customer.getSetting('processing.GpsLocationState'), 'enabled')
    },

    /**
     * Search criteria to return audit log entries associated with this route
     *
     * @return object
     */
    routeAuditLogSearchCriteria () {
      return {
        resourceType: 'route',
        primaryKey: this.route.id,
        createdAfter: this.route.created
      }
    },

    /**
     * Search criteria to return audit log entries associated with the route
     * products on this route
     *
     * @return object
     */
    routeProductAuditLogSearchCriteria () {
      return {
        resourceType: 'route_product',
        primaryKey: this.auditLogRouteProductIds.join(','),
        createdAfter: this.route.created
      }
    },

    /**
     * Search criteria to return notifications associated with this route
     *
     * @return object
     */
    notificationSearchCriteria () {
      return {
        resourceType: 'Route',
        primaryKey: this.route.id,
        createdAfter: Moment(this.route.created).startOf('day')
      }
    },

    /**
     * Whether to display the tab with the RouteProductRecordedTemperatures
     * @returns {*}
     */
    showRouteProductRecordedTemperatures () {
      return this.customerContext.getSetting('enableRouteProductRecordedTemperatures')
    }
  },
  watch: {
    /**
     * Keep the URL fragment on the tab being shown, so the page can be copied, bookmarked and reloaded in place.
     */
    activeTab (newValue) {
      this.writeTabToHash(newValue)
    },
    '$route.hash': function () {
      this.syncTabFromHash()
    }
  },
  created () {
    this.fetchRoute()
    this.restoreState()
  },
  beforeDestroy () {
    this.componentDestroyed = true
  }
}
</script>

<style scoped lang="scss">
.command-links {
  width: 400px;
}
.tab-content {
  min-height: 350px;
}
.not-set {
  color: #9e9e9e;
  font-style: italic;
  text-transform: capitalize;
  white-space: nowrap;
}
</style>
